import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchActiveGame, fetchGame, isGone } from '@/shared/game/api'
import { forgetGame, markOver, myGames } from '@/shared/game/storage'
import { isLocalGame } from '@/shared/local/store'

/** Партия, в которую есть смысл вернуться. */
export type Resumable = {
  id: string
  /** Чем партия подписана в кнопке: имя или состав стола. */
  label: string
  /** Когда в неё последний раз ходили. */
  at: number
  /** Партия за одним устройством: сервера у неё нет. */
  local: boolean
}

/**
 * Сколько записей из localStorage проверяем за один раз.
 *
 * Своих партий в браузере накапливается сколько угодно, а нужна первая
 * живая. Дальше третьей не идём: партий подряд убитых уборкой больше двух
 * не бывает, а забытые записи не возвращаются — следующий заход начнёт
 * с того места, где этот остановился.
 */
const PROBES = 3

export function resumableKey(userId: number | null): [string, number | null] {
  return ['resumable', userId]
}

/**
 * Как подписан стол в кнопке.
 *
 * Имени у партии нет, поэтому узнают её по соперникам. Компьютеры считаются
 * вместе: «с компьютером» говорит о партии больше, чем три имени ботов.
 */
function labelOf(players: { name: string; bot: string | null }[]): string {
  const people = players.filter((player) => player.bot === null).map((player) => player.name)
  const bots = players.length - people.length

  const parts = [...people.slice(1), ...(bots > 0 ? ['компьютер'] : [])]

  return parts.length === 0 ? 'партия' : `с ${parts.join(', ')}`
}

async function resolve(userId: number | null): Promise<Resumable | null> {
  if (userId !== null) {
    const active = await fetchActiveGame()

    if (active !== null) {
      return {
        id: active.id,
        label: labelOf(active.players),
        at: active.updatedAt === null ? Date.now() : Date.parse(active.updatedAt),
        local: false,
      }
    }
  }

  for (const saved of myGames()
    .filter((game) => game.over !== true)
    .slice(0, PROBES)) {
    if (isLocalGame(saved.id)) {
      return { id: saved.id, label: saved.name || 'партия', at: saved.at, local: true }
    }

    try {
      const view = await fetchGame(saved.id)

      if (view.status === 'lobby' || view.status === 'playing') {
        return { id: saved.id, label: labelOf(view.players), at: saved.at, local: false }
      }

      // Доиграна или брошена: продолжать нечего, и предлагать её больше
      // не надо — иначе она всплывала бы на главной каждый раз.
      markOver(saved.id)
    } catch (error) {
      if (!isGone(error)) throw error

      forgetGame(saved.id)
    }
  }

  return null
}

/**
 * Есть ли куда вернуться — то, из чего собирается кнопка «Продолжить».
 *
 * Раньше она собиралась из одного localStorage и ничего не проверяла: партию
 * давно убрал games:prune (незапущенный стол живёт двадцать минут), а запись
 * о ней в браузере осталась навсегда. Кнопка была, а по нажатию приходило
 * «партия не найдена» — ровно потому, что записи о партиях и сами партии
 * живут в разных местах и по разным правилам.
 *
 * Поэтому проверка есть всегда, и правды две:
 *
 * у вошедшего главная — сервер (GET /games/active): он знает партию человека
 * и с другого устройства, где localStorage пуст;
 *
 * у гостя сервер такой партии не знает вовсе — секрет привязан к одной
 * партии, — и остаётся localStorage, но каждая запись из него сверяется
 * запросом. Пропавшая забывается, доигранная помечается, и в следующий раз
 * их уже не предложат.
 *
 * Партия за одним устройством ни там, ни там: она целиком в браузере, и
 * проверять её нечем и незачем.
 *
 * undefined, пока проверяем: кнопку до ответа лучше не показывать вовсе,
 * чем показать и убрать.
 */
export function useResumable(userId: number | null) {
  return useQuery({
    queryKey: resumableKey(userId),
    queryFn: () => resolve(userId),
    /*
     * Свежесть проверяется каждый раз, когда на главную возвращаются: партию
     * могли бросить в другой вкладке, доиграть или начать заново, а кнопка
     * «Продолжить» обещает конкретный адрес.
     */
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    // Пропавшая партия — не повод для повторов: 404 разбирается внутри
    // resolve, а до сюда доходит только настоящая поломка связи.
    retry: false,
  })
}

/** Забыть, что было решено про «Продолжить»: партию бросили или начали новую. */
export function useForgetResumable(userId: number | null): () => Promise<void> {
  const client = useQueryClient()

  return () => client.invalidateQueries({ queryKey: resumableKey(userId) })
}
